import React from 'react';
import { ChevronLeft, Mail, Phone, Zap, Home } from 'lucide-react';

const MobileDetailsPanel = ({ chat, onBack, loading = false }) => {

    // Handle loading state
    if (loading) {
        return (
            <div className="flex flex-col h-full bg-white items-center justify-center">
                <p className="text-sm text-stone-500">Loading details...</p>
            </div>
        );
    }

    // Handle undefined chat
    if (!chat) {
        return (
            <div className="flex flex-col h-full bg-white items-center justify-center">
                <p className="text-sm text-stone-500">No details available</p>
            </div>
        );
    }

    // Extract data from Firebase lead structure (matching LeadDrawer pattern)
    const leadName = chat.fullName || chat.name || 'Unknown';
    const contactEmail = chat.email || null;
    const contactPhone = chat.phone || null;
    const leadStatus = chat.status || 'Active';

    // Interested Properties - matches LeadDrawer's interestedProperties array format
    const interestedProperties = Array.isArray(chat.interestedProperties) ? chat.interestedProperties : [];

    // Notes - lead schema stores notes as a string, not array
    const notesText = typeof chat.notes === 'string' ? chat.notes : '';

    // Tags
    const tags = Array.isArray(chat.tags) ? chat.tags : [];

    // Automation data (if present from conversation or lead)
    const automationName = chat.automation?.name || null;
    const automationStep = chat.automation?.step || '';
    const automationAction = chat.automation?.nextAction || '';

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-notion-border sticky top-0 bg-white">
                <button onClick={onBack} className="text-stone-600 hover:text-stone-800">
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h2 className="text-lg font-semibold text-notion-text">{leadName}</h2>
                <div className="w-6"></div>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 space-y-6 overflow-y-auto">
                {/* Status Display */}
                {leadStatus && (
                    <div>
                        <p className="text-sm text-stone-500">{leadStatus === 'Active' ? 'Active Nurture' : leadStatus}</p>
                    </div>
                )}

                {/* Contact Info */}
                {(contactEmail || contactPhone) && (
                    <div>
                        <h3 className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Contact Info</h3>
                        <div className="space-y-3">
                            {contactEmail && (
                                <div className="flex items-center gap-3">
                                    <Mail className="w-5 h-5 text-stone-400 flex-shrink-0" />
                                    <span className="text-sm text-notion-text">{contactEmail}</span>
                                </div>
                            )}
                            {contactPhone && (
                                <div className="flex items-center gap-3">
                                    <Phone className="w-5 h-5 text-stone-400 flex-shrink-0" />
                                    <span className="text-sm text-notion-text">{contactPhone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Automation - Only show if automation data exists */}
                {automationName && (
                    <div>
                        <h3 className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Automation</h3>
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                                <Zap className="w-4 h-4 text-amber-500" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-notion-text">{automationName}</p>
                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                </div>
                                {automationStep && (
                                    <p className="text-xs text-stone-400 mt-0.5">{automationStep} • {automationAction}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Interested Properties - Matches LeadDrawer pattern */}
                {interestedProperties.length > 0 && (
                    <div>
                        <h3 className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Interest</h3>
                        <div className="space-y-2">
                            {interestedProperties.map((prop, index) => (
                                <div key={prop.id || index} className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                                        <Home className="w-4 h-4 text-stone-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-notion-text">{prop.address || 'Property'}</p>
                                        {prop.price && (
                                            <p className="text-xs text-stone-400 mt-0.5">{prop.price}{prop.details ? ` • ${prop.details}` : ''}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Notes - Lead schema stores notes as string */}
                {notesText && (
                    <div>
                        <h3 className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Notes</h3>
                        <div className="border-l-2 border-amber-400 pl-3 py-1">
                            <p className="text-sm text-stone-600">"{notesText}"</p>
                        </div>
                    </div>
                )}

                {/* Tags - if present */}
                {tags.length > 0 && (
                    <div>
                        <h3 className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Tags</h3>
                        <div className="flex flex-wrap gap-1.5">
                            {tags.map((tag, index) => (
                                <span key={index} className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded">
                                    {tag.startsWith('#') ? tag : `#${tag}`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileDetailsPanel;